import { defineStore } from 'pinia';
import { ref } from 'vue';

import {
  createCustomerMapping,
  deleteCustomerMapping,
  fetchCustomerMappings,
  updateCustomerMapping,
} from '@/services/customerMappingService';
import type { CustomerMapping, CustomerMappingInput } from '@/types/customerMapping';
import { extractErrorMessage } from '@/utils/apiError';

const byCode = (a: CustomerMapping, b: CustomerMapping): number => a.code.localeCompare(b.code);

export const useCustomerMappingStore = defineStore('customerMapping', () => {
  const items = ref<CustomerMapping[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const loaded = ref(false);

  async function load(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      items.value = await fetchCustomerMappings();
      loaded.value = true;
    } catch (loadError) {
      error.value = extractErrorMessage(loadError);
    } finally {
      loading.value = false;
    }
  }

  async function create(input: CustomerMappingInput): Promise<void> {
    const created = await createCustomerMapping(input);
    items.value = [...items.value, created].sort(byCode);
  }

  async function update(id: string, input: Partial<CustomerMappingInput>): Promise<void> {
    const updated = await updateCustomerMapping(id, input);
    items.value = items.value.map((mapping) => (mapping.id === id ? updated : mapping)).sort(byCode);
  }

  async function remove(id: string): Promise<void> {
    await deleteCustomerMapping(id);
    items.value = items.value.filter((mapping) => mapping.id !== id);
  }

  return { items, loading, error, loaded, load, create, update, remove };
});
